import React from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import Select, { InputActionMeta, Props as SelectProps } from "react-select";
import CreatableSelect from "react-select/creatable";
import { filtersConfig, OptionType } from "../utils/reactSelectUtils";
import { useAppSelector } from "@src/store/hooks";
import { clx } from "../utils/stringUtils";
import styles from "./Select.module.scss";

interface SelectYupProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	options: Array<OptionType>;
	placeholder?: string;
	label?: string;
	isLoading?: boolean;
	isDisabled?: boolean;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onInputChange?: (value: string) => void;
	onValueChange?: (value: string) => void;
	allowCustomInput?: boolean;
	maxVisibleOptions?: number;
	hideDropdownIndicator?: boolean;
	menuMaxHeight?: number;
	formatValueLabel?: (value: string) => string;
}

export const SelectYup = <T extends FieldValues>({
	options,
	name,
	control,
	placeholder,
	isLoading,
	isDisabled,
	label,
	onFocus,
	onInputChange: onInputChangeCallback,
	onValueChange,
	allowCustomInput = false,
	maxVisibleOptions,
	hideDropdownIndicator = false,
	menuMaxHeight,
	formatValueLabel,
}: SelectYupProps<T>) => {
	const { field, fieldState } = useController({ name, control });
	const [inputValue, setInputValue] = React.useState("");
	const rootRef = React.useRef<HTMLDivElement>(null);

	const fieldValue = field?.value;
	const selectedOption =
		fieldValue === undefined || fieldValue === null || fieldValue === ""
			? null
			: options.find((opt) => String(opt.value) === String(fieldValue));
	const value =
		selectedOption ?? (fieldValue ? { label: formatValueLabel?.(String(fieldValue)) ?? fieldValue, value: fieldValue } : null);

	const { isDirty, invalid } = fieldState;

	const innerTernary = field.value && isDirty ? "success" : "none";
	const inputStatus = invalid ? "error" : innerTernary;
	const theme = useAppSelector((state) => state.settings.theme);

	const customStyles = filtersConfig(inputStatus, theme, undefined, menuMaxHeight);

	const handleInputChange = (newValue: string, meta: InputActionMeta) => {
		if (allowCustomInput && (meta.action === "input-blur" || meta.action === "menu-close")) return;
		setInputValue(newValue);
		if (allowCustomInput && onInputChangeCallback) {
			onInputChangeCallback(newValue);
		}
	};

	const findExactOption = (raw: string) => {
		const term = raw.trim().toLocaleLowerCase();
		if (!term) return undefined;
		return options.find((option) => String(option.label).toLocaleLowerCase() === term);
	};

	const submitForm = () => rootRef.current?.closest("form")?.requestSubmit();
	const submitOnSelectRef = React.useRef(false);

	const commitCustomInput = (raw: string) => {
		const term = raw.trim();
		setInputValue("");
		if (!term) return false;
		const nextValue = String(findExactOption(term)?.value ?? term);
		field.onChange(nextValue);
		onValueChange?.(nextValue);
		return true;
	};

	const handleCreateOption = (raw: string) => {
		submitOnSelectRef.current = false;
		if (commitCustomInput(raw)) submitForm();
	};

	const isCapped = typeof maxVisibleOptions === "number";
	const tracksInput = allowCustomInput || isCapped;

	const visibleOptions = React.useMemo(() => {
		if (!isCapped) return options;
		const term = inputValue.trim().toLocaleLowerCase();
		const matching = term ? options.filter((option) => String(option.label).toLocaleLowerCase().includes(term)) : options;
		const isExact = (option: OptionType) => String(option.label).toLocaleLowerCase() === term;
		const ordered = term ? [...matching.filter(isExact), ...matching.filter((option) => !isExact(option))] : matching;
		return ordered.slice(0, maxVisibleOptions);
	}, [isCapped, options, inputValue, maxVisibleOptions]);

	const isValidCustomInput = (raw: string) => Boolean(raw.trim()) && !findExactOption(raw);

	const selectProps: SelectProps<OptionType, false> = {
		isClearable: true,
		isLoading: isLoading ?? false,
		isDisabled: isDisabled ?? false,
		options: visibleOptions,
		placeholder,
		onChange: (data) => {
			const nextValue = data?.value ?? "";
			field.onChange(nextValue);
			onValueChange?.(String(nextValue));
			if (allowCustomInput) {
				setInputValue("");
				if (submitOnSelectRef.current && nextValue) submitForm();
			}
			submitOnSelectRef.current = false;
		},
		onKeyDown: allowCustomInput
			? (event) => {
					submitOnSelectRef.current = event.key === "Enter";
				}
			: undefined,
		onFocus,
		onBlur: allowCustomInput
			? () => {
					submitOnSelectRef.current = false;
					commitCustomInput(inputValue);
				}
			: undefined,
		onInputChange: tracksInput ? handleInputChange : undefined,
		inputValue: allowCustomInput ? inputValue : undefined,
		value,
		isMulti: false,
		styles: customStyles,
		filterOption: isCapped ? null : undefined,
		formatOptionLabel: (option, meta) =>
			option.fieldLabel && meta.context === "menu" ? (
				<span className={styles.optionRow}>
					<span className={styles.optionValue}>{option.label}</span>
					<span className={styles.optionField}>({option.fieldLabel})</span>
				</span>
			) : (
				option.label
			),
		components: { IndicatorSeparator: null, ...(hideDropdownIndicator ? { DropdownIndicator: null } : {}) },
	};

	return (
		<div ref={rootRef} className={clx({ [styles.main]: true })}>
			{label && <div className={styles.label}>{label}</div>}
			{allowCustomInput ? (
				<CreatableSelect<OptionType, false>
					{...selectProps}
					onCreateOption={handleCreateOption}
					isValidNewOption={isValidCustomInput}
					formatCreateLabel={(raw) => `Search "${raw.trim()}"`}
					createOptionPosition="first"
				/>
			) : (
				<Select<OptionType, false> {...selectProps} />
			)}
		</div>
	);
};
